/**
 * CSV template of the list upload page (TASK-353, moved out of
 * DatasetUploadPage in KAN-727).
 *
 * Headers are the schema labels, so they follow any schema change. The
 * downloadable file has one example row; the on-page example has two.
 */
import { useCallback, useMemo } from 'react';
import { exampleValueForField } from '@/lib/datasetTemplate';
import type { SchemaField } from '@/types/dataset';

export function useCsvTemplate(schema: SchemaField[]) {
  const templateHeaders = useMemo(() => schema.map((f) => f.label || f.name), [schema]);
  const exampleRow = useMemo(() => schema.map(exampleValueForField), [schema]);

  const previewLines = useMemo(() => {
    const row2 = schema
      // TASK-413 (AC-3): no leading "+" here either, same Excel-formula trap.
      .map((f) => (f.type === 'phone' ? '89997654321' : exampleValueForField(f)))
      .join(',');
    return [templateHeaders.join(','), exampleRow.join(','), row2];
  }, [schema, templateHeaders, exampleRow]);

  // Built on the front via a Blob, no server round-trip; BOM for Excel.
  const downloadTemplate = useCallback(() => {
    if (schema.length === 0) return;
    const csv = `${templateHeaders.join(',')}\n${exampleRow.join(',')}\n`;
    const blob = new Blob([String.fromCharCode(0xfeff) + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'template.csv';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, [schema, templateHeaders, exampleRow]);

  return { previewLines, downloadTemplate };
}
